import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Typography, 
  Grid, 
  Divider, 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { styled } from '@mui/system';

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: theme.spacing(4),
  maxWidth: '1200px',
  margin: '20px auto',
  fontFamily: 'Roboto, sans-serif',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  border: '1px solid #E1E1E1',
}));

const Header = styled(Typography)(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 700,
  color: '#1D3557',
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

const SubHeader = styled(Typography)(({ theme }) => ({
  fontSize: '22px',
  color: '#457b9d',
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: '#F9F9F9',
  borderRadius: '8px',
}));

const DocumentCount = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#1D3557',
  textAlign: 'center',
  margin: theme.spacing(2, 0),
}));

const ViewCustomer = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const navigate = useNavigate();


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const parsedToken = JSON.parse(atob(token.split('.')[1])); // Decode JWT token
        setUsername(parsedToken.username); // Set username from the token
      } catch (error) {
        console.error('Failed to parse token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const customerResponse = await axios.get(`https://apidashboard.eurekagroup.id/customers/get/${id}`);
        setCustomer(customerResponse.data);

        const categoryResponse = await axios.get('https://apidashboard.eurekagroup.id/documents');
        setDocumentCategories(categoryResponse.data);

        const userResponse = await axios.get('https://apidashboard.eurekagroup.id/employee');
        setUsers(userResponse.data);
      } catch (error) {
        console.error("Error fetching data", error);
        setCustomer(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Customer tidak ditemukan
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/kontak/customer')}
          startIcon={<ArrowBackIcon />}
        >
          Kembali ke Daftar Customer
        </Button>
      </Box>
    );
  }

  const handleDocumentClick = (docId) => {
    navigate(`/document/ViewDocument/${docId}`);
  };

  const handleBackToCustomer = () => {
    navigate('/kontak/customer');
  };

  const handleEditCustomer = () => {
    navigate(`/kontak/customer/edit/${customer._id}`);
  };

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {customer.customer_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Detail informasi customer
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="info" 
                startIcon={<EditIcon />} 
                onClick={handleEditCustomer}
                className="hover:scale-105 transition-all duration-300"
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit
              </Button>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBackToCustomer}
                className="hover:scale-105 transition-all duration-300"
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Kembali
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Status dan Info Utama */}
          <Box sx={{ mb: 3, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={customer.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                    color={customer.is_active === 1 ? 'success' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Business Unit: <strong>{customer.id_bu?.bu_name || 'N/A'}</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Ditambahkan: <strong>{new Date(customer.date_added).toLocaleDateString('id-ID')}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Diperbarui: <strong>{new Date(customer.date_modified).toLocaleDateString('id-ID')}</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Informasi PIC */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              👤 Informasi PIC (Person In Charge)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Gelar PIC
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.pic_title || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Nama Belakang PIC
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.pic_last_name || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Posisi PIC
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.pic_position || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Internal PIC
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.internal_pic || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Informasi Kontak */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              📞 Informasi Kontak
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Telepon
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.phone || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Mobile
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.mobile || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.email || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Fax
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.fax || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Informasi Bisnis */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              🏢 Informasi Bisnis
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Tipe Bisnis
                  </Typography>
                  <Chip 
                    label={customer.business_type || 'N/A'}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Tahun Didirikan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.year_founded || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Term of Payment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.term_of_payment || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Max Credit Limit
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.max_credit_limit ? `Rp ${parseInt(customer.max_credit_limit).toLocaleString('id-ID')}` : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Alamat */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              🏠 Alamat
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Alamat Lengkap
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.address || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Alamat Penagihan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.billing_address || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Kota
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.id_city || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Provinsi
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.id_province || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Kecamatan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.id_subdistrict || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Informasi Bank */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              🏦 Informasi Bank
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Nama Bank
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.bank_account_name || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Nomor Rekening
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.bank_account_number || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Nama Pemegang Rekening
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.bank_account_nameholder || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Cabang Bank
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.bank_account_brench || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Tax No
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customer.tax_no || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Dokumen */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              📄 Dokumen ({customer.document_count || 0})
            </Typography>
            {customer.documents && customer.documents.length > 0 ? (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nama Dokumen</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Kategori</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Diupload Oleh</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Tanggal Upload</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.documents.map((doc) => (
                      <TableRow key={doc._id} hover>
                        <TableCell>{doc.document_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={doc.category_name || 'Unknown Category'}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{doc.full_name || '-'}</TableCell>
                        <TableCell>{new Date(doc.date_added).toLocaleDateString('id-ID')}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleDocumentClick(doc._id)}
                            sx={{ borderRadius: 2 }}
                          >
                            Lihat
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Belum ada dokumen yang diupload
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewCustomer;
